import { api } from './api';
import {
  ReadinessConfidenceIndicator,
  ReadinessStatus,
  ConfidenceLevel,
} from '../types/readinessConfidence.types';

export const readinessConfidenceService = {
  /**
   * Get readiness × confidence indicator for an experiment
   */
  async getTrustIndicator(experimentId: number): Promise<ReadinessConfidenceIndicator> {
    const response = await api.get<ReadinessConfidenceIndicator>(
      `/coreapi/experiments/${experimentId}/trust-indicator`
    );
    return response.data;
  },

  /**
   * Get trust indicator for a page
   */
  async getPageTrustIndicator(pageId: number): Promise<ReadinessConfidenceIndicator> {
    const response = await api.get<ReadinessConfidenceIndicator>(
      `/coreapi/pages/${pageId}/trust-indicator`
    );
    return response.data;
  },

  /**
   * Compute readiness from validation results
   */
  async computeReadiness(pageIds: number[]): Promise<{
    readiness: ReadinessStatus;
    reason: string;
    evidenceLink: string;
  }> {
    const response = await api.post(
      `/readiness/compute`,
      { pageIds }
    );
    return response.data;
  },

  /**
   * Compute confidence from experiment data
   */
  async computeConfidence(experimentId: number): Promise<{
    confidence: ConfidenceLevel;
    reason: string;
    score: number;
    evidenceLink: string;
  }> {
    const response = await api.post(
      `/confidence/compute`,
      { experimentId }
    );
    return response.data;
  },

  /**
   * Override trust indicator (admin only)
   */
  async overrideTrustIndicator(
    experimentId: number,
    override: {
      readiness?: ReadinessStatus;
      confidence?: ConfidenceLevel;
      reason: string;
    }
  ): Promise<ReadinessConfidenceIndicator> {
    const response = await api.post(
      `/experiments/${experimentId}/trust-indicator/override`,
      override
    );
    return response.data;
  },

  /**
   * Get override history for an experiment
   */
  async getOverrideHistory(
    experimentId: number,
    limit: number = 10
  ): Promise<Array<{
    id: number;
    readiness?: ReadinessStatus;
    confidence?: ConfidenceLevel;
    reason: string;
    overriddenBy: string;
    overriddenAt: string;
  }>> {
    const response = await api.get(
      `/coreapi/experiments/${experimentId}/trust-indicator/overrides`,
      { params: { limit } }
    );
    return response.data.content || [];
  },

  /**
   * Batch compute trust indicators for multiple experiments
   */
  async computeBatch(experimentIds: number[]): Promise<
    Record<number, ReadinessConfidenceIndicator>
  > {
    const response = await api.post(
      `/trust-indicator/batch`,
      { experimentIds }
    );
    return response.data;
  },

  /**
   * Get readiness/confidence thresholds and configuration
   */
  async getConfiguration(): Promise<{
    readinessThresholds: {
      pass: string[];
      warn: string[];
      fail: string[];
    };
    confidenceThresholds: {
      high: {
        pValue: number;
        minSampleSize: number;
        minEffectSize: number;
      };
      medium: {
        pValue: number;
        minSampleSize: number;
      };
      low: {
        pValue: number;
      };
    };
  }> {
    const response = await api.get(`/coreapi/trust-indicator/config`);
    return response.data;
  },
};
